import { apiFetchJSON } from '../../core/api.js';
import { getTg } from '../../core/telegram.js';
import { showView } from '../../app/navigation.js';
import { getIsOwnerApp } from '../../app/app-state.js';
import type {
  OwnerChatClearThreadResponse,
  OwnerChatThread,
  OwnerChatThreadsResponse
} from '../../types/api.js';
import {
  getOwnerChatActiveConversationUserId,
  setOwnerChatActiveConversationUserId,
  getOwnerChatClearMode,
  setOwnerChatSelectedDisplayName,
  setOwnerChatMode,
  setOwnerChatActiveOrderId,
  setOwnerChatActiveOrderTitle,
  setOwnerChatActiveCustomerUserId,
  getOwnerChatListTab,
  setOwnerChatListTab,
  type OwnerChatMode
} from './ui-state.js';
import {
  getOwnerChatViewerId,
  ownerChatThreadsEl,
  ownerChatMessagesEl,
  ownerChatComposerEl,
  ownerChatSubtitleEl
} from './dom.js';
import { resetOwnerChatLastSeenId } from './session.js';
import { clearOwnerChatMessages, formatOwnerChatTime } from './messages.js';
import { orderChatShortId, parseOrderChatThreadId, resolveThreadKind } from '../../lib/order-chat-thread.js';

let ownerChatTabsBound = false;

function syncOwnerChatTabUi(tab: OwnerChatMode): void {
  document.querySelectorAll('.chat-owner-tab').forEach(function (el) {
    if (!(el instanceof HTMLElement)) return;
    const isActive = String(el.dataset.chatTab || '') === tab;
    el.classList.toggle('chat-owner-tab-active', isActive);
    el.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
}

export function bindOwnerChatTabs(onTabChange: () => void): void {
  if (ownerChatTabsBound) return;
  ownerChatTabsBound = true;
  document.querySelectorAll('.chat-owner-tab').forEach(function (el) {
    if (!(el instanceof HTMLElement)) return;
    el.addEventListener('click', function () {
      const tab = String(el.dataset.chatTab || '') === 'consult' ? 'consult' : 'order';
      if (tab === getOwnerChatListTab()) return;
      setOwnerChatListTab(tab);
      syncOwnerChatTabUi(tab);
      onTabChange();
    });
  });
}

export function renderOwnerChatThreads(threads: OwnerChatThread[]) {
  const listEl = ownerChatThreadsEl;
  if (!listEl) return;
  listEl.innerHTML = '';

  const tab = getOwnerChatListTab();
  const filtered = (Array.isArray(threads) ? threads : []).filter(function (t) {
    return resolveThreadKind(String(t?.conversationUserId || ''), t?.threadKind) === tab;
  });

  if (!filtered.length) {
    const empty = document.createElement('div');
    empty.className = 'chat-thread-empty';
    empty.textContent =
      tab === 'order'
        ? 'Пока нет чатов по заказам.'
        : getOwnerChatClearMode()
          ? 'Нет диалогов для очистки.'
          : 'Пока нет сообщений в консультации.';
    listEl.appendChild(empty);
    return;
  }

  filtered.forEach((t) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chat-thread-item';
    btn.setAttribute('data-conversation-user-id', String(t.conversationUserId));

    const isActive =
      getOwnerChatActiveConversationUserId() &&
      String(t.conversationUserId) === String(getOwnerChatActiveConversationUserId());
    if (isActive) btn.classList.add('chat-thread-item-active');

    const isOrder = resolveThreadKind(String(t.conversationUserId || ''), t.threadKind) === 'order';
    const parsed = parseOrderChatThreadId(String(t.conversationUserId || ''));
    const usernameClean =
      t.username && String(t.username).trim() ? String(t.username).trim().replace(/^@/, '') : '';
    const displayName = isOrder
      ? 'Заказ #' + orderChatShortId(t.orderId || parsed?.orderId || '')
      : usernameClean
        ? '@' + usernameClean
        : 'Пользователь';
    const subLine = isOrder
      ? usernameClean
        ? '@' + usernameClean
        : t.orderTitle
          ? String(t.orderTitle)
          : 'Клиент'
      : '';

    const initials = (function () {
      if (isOrder) return '#';
      const s = String(usernameClean || displayName).trim();
      if (!s) return '?';
      const parts = s.split(/\s+/);
      const first = parts[0] && parts[0][0] ? parts[0][0] : s[0];
      const last =
        parts.length > 1 && parts[parts.length - 1][0] ? parts[parts.length - 1][0] : s.length > 1 ? s[1] : '';
      return (first + last).toUpperCase();
    })();

    const avatar = document.createElement('div');
    avatar.className = 'chat-thread-avatar' + (isOrder ? ' chat-thread-avatar--order' : '');
    avatar.textContent = initials;

    const contentCol = document.createElement('div');
    contentCol.className = 'chat-thread-content';

    const meta = document.createElement('div');
    meta.className = 'chat-thread-meta';

    const nameEl = document.createElement('div');
    nameEl.className = 'chat-thread-name';
    nameEl.textContent = displayName;

    const timeEl = document.createElement('div');
    timeEl.className = 'chat-thread-time';
    timeEl.textContent = t.lastAt ? formatOwnerChatTime(t.lastAt) : '';

    meta.appendChild(nameEl);
    meta.appendChild(timeEl);

    const bubble = document.createElement('div');
    bubble.className = 'chat-thread-bubble';
    const previewText = t.lastText && String(t.lastText).trim() ? String(t.lastText).trim() : '';
    bubble.textContent = previewText;

    if (String(t.lastFromRole || '') === 'owner') bubble.classList.add('chat-thread-bubble-me');
    else bubble.classList.add('chat-thread-bubble-them');

    contentCol.appendChild(meta);
    if (subLine) {
      const subEl = document.createElement('div');
      subEl.className = 'chat-thread-sub';
      subEl.textContent = subLine;
      contentCol.appendChild(subEl);
    }
    contentCol.appendChild(bubble);

    btn.appendChild(avatar);
    btn.appendChild(contentCol);

    btn.addEventListener('click', () => {
      if (getOwnerChatClearMode()) {
        const title = displayName;
        const question = `Очистить историю чата «${title}»?`;
        const doClear = async () => {
          const ok = await clearOwnerChatThread(String(t.conversationUserId));
          if (!ok) {
            try {
              var tgClear = getTg();
              if (tgClear && typeof tgClear.showPopup === 'function') {
                tgClear.showPopup({
                  title: 'Ошибка',
                  message: 'Не удалось очистить чат. Попробуйте позже.',
                  buttons: [{ type: 'ok' }]
                });
              }
            } catch (_) {}
            return;
          }
          if (String(getOwnerChatActiveConversationUserId() || '') === String(t.conversationUserId)) {
            setOwnerChatActiveConversationUserId(null);
            setOwnerChatSelectedDisplayName('');
            resetOwnerChatLastSeenId();
            clearOwnerChatMessages();
            if (ownerChatMessagesEl) ownerChatMessagesEl.style.display = 'none';
            if (ownerChatComposerEl) ownerChatComposerEl.style.display = 'none';
            if (ownerChatSubtitleEl) ownerChatSubtitleEl.textContent = '';
          }
          const fresh = await loadOwnerChatThreads();
          renderOwnerChatThreads(fresh);
          try {
            var tgDone = getTg();
            if (tgDone && typeof tgDone.showPopup === 'function') {
              tgDone.showPopup({
                title: 'Готово',
                message: 'История выбранного чата очищена.',
                buttons: [{ type: 'ok' }]
              });
            }
          } catch (_) {}
        };
        try {
          var tgConfirm = getTg();
          if (tgConfirm && typeof tgConfirm.showConfirm === 'function') {
            tgConfirm.showConfirm(question, function (confirmed: boolean) {
              if (confirmed) doClear();
            });
            return;
          }
        } catch (_) {}
        if (window.confirm(question)) doClear();
        return;
      }

      setOwnerChatActiveConversationUserId(String(t.conversationUserId));
      if (isOrder) {
        setOwnerChatMode('order');
        setOwnerChatActiveOrderId(t.orderId || parsed?.orderId || '');
        setOwnerChatActiveOrderTitle(t.orderTitle || '');
        setOwnerChatActiveCustomerUserId(t.customerUserId || parsed?.userId || '');
        setOwnerChatSelectedDisplayName(displayName + (subLine ? ' · ' + subLine : ''));
      } else {
        setOwnerChatMode('consult');
        setOwnerChatActiveOrderId(null);
        setOwnerChatActiveOrderTitle('');
        setOwnerChatActiveCustomerUserId(null);
        setOwnerChatSelectedDisplayName(usernameClean ? '@' + usernameClean : displayName);
      }
      resetOwnerChatLastSeenId();
      if (ownerChatSubtitleEl) {
        ownerChatSubtitleEl.textContent = isOrder
          ? displayName + (subLine ? ' · ' + subLine : '')
          : `Диалог с ${usernameClean ? '@' + usernameClean : displayName}`;
      }
      clearOwnerChatMessages();
      renderOwnerChatThreads(threads);
      showView('view-consult', 'right');
    });

    listEl.appendChild(btn);
  });
}

export async function loadOwnerChatThreads(): Promise<OwnerChatThread[]> {
  if (!getIsOwnerApp() || !getOwnerChatViewerId()) return [];
  const tab = getOwnerChatListTab();
  const data = await apiFetchJSON<OwnerChatThreadsResponse>(
    '/api/owner-chat/threads?kind=' + encodeURIComponent(tab),
    { method: 'GET' }
  ).catch(() => null);

  if (!data || !data.ok) return [];
  return Array.isArray(data.threads) ? data.threads : [];
}

export async function clearOwnerChatThread(conversationUserId: string): Promise<boolean> {
  if (!getIsOwnerApp() || !getOwnerChatViewerId() || !conversationUserId) return false;
  const data = await apiFetchJSON<OwnerChatClearThreadResponse>('/api/owner-chat/clear-thread', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationUserId: String(conversationUserId) })
  }).catch(() => null);
  return !!(data && data.ok);
}

export function initOwnerChatTabsUi(): void {
  syncOwnerChatTabUi(getOwnerChatListTab());
}
